import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import InstructorLayout from "@/components/InstructorLayout";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Loader2, Video, FileText, ListChecks, Presentation,
  BookOpen, Send, ClipboardCheck, GraduationCap, Wand2, Save, ChevronRight,
} from "lucide-react";
import FileUploader, { FileTile } from "@/components/FileUploader";

const LESSON_TYPES = [
  { value: "video", label: "Video (YouTube/Vimeo)", Icon: Video },
  { value: "text", label: "Texto / markdown", Icon: FileText },
  { value: "case_study", label: "Caso de estudio", Icon: ClipboardCheck },
  { value: "presentation", label: "Presentación", Icon: Presentation },
  { value: "quiz", label: "Quiz", Icon: ListChecks },
  { value: "exam", label: "Examen", Icon: GraduationCap },
  { value: "bibliography", label: "Bibliografía", Icon: BookOpen },
  { value: "live", label: "Sesión vivo", Icon: Video },
];

const STATUS_BADGE = {
  draft: "bg-gray-500/20 text-gray-300",
  pending_review: "bg-amber-500/20 text-amber-300",
  approved: "bg-emerald-500/20 text-emerald-300",
  rejected: "bg-red-500/20 text-red-300",
};

export default function InstructorProgramEditor() {
  const { pid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/instructor/programs/${pid}/editor`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [pid]);

  const addModule = async () => {
    const title = prompt("Título del módulo:");
    if (!title) return;
    try {
      await api.post(`/instructor/programs/${pid}/modules`, { title, order: (data.modules.length + 1), description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const addLesson = async (moduleId, type) => {
    const title = prompt(`Título de la lección (${type})`);
    if (!title) return;
    const defaults = {
      video: { video_source_type: "youtube", video_url: "" },
      text: { body_markdown: "" },
      case_study: { body_markdown: "", discussion_prompts: [], reference_files: [] },
      presentation: { slides: [] },
      quiz: { questions: [], passing_score: 70 },
      exam: { questions: [], passing_score: 70, max_attempts: 1, is_exam: true },
      bibliography: { references: [] },
      live: { platform: "dcc_room" },
    }[type] || {};
    try {
      // For video with empty URL, create as text draft first, then convert
      const payload = type === "video" ? { body_markdown: "Pendiente: pega una URL de YouTube o Vimeo." } : defaults;
      const actualType = type === "video" ? "text" : type;  // avoid immediate validation error
      const r = await api.post(`/instructor/modules/${moduleId}/lessons`, {
        title, order: 0, type: actualType, payload, duration_min: 5, source: "manual",
      });
      await load();
      setSelectedLesson({ id: r.data.id, ...r.data });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const deleteModule = async (moduleId) => {
    if (!confirm("¿Eliminar este módulo y todas sus lecciones?")) return;
    try {
      await api.delete(`/instructor/modules/${moduleId}`);
      setSelectedLesson(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const deleteLesson = async (lessonId) => {
    if (!confirm("¿Eliminar esta lección?")) return;
    try {
      await api.delete(`/instructor/lessons/${lessonId}`);
      setSelectedLesson(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const submitForReview = async () => {
    if (!confirm("¿Enviar programa a revisión editorial? No podrás editarlo hasta que reciban la respuesta.")) return;
    setSubmitting(true);
    try {
      await api.post(`/instructor/programs/${pid}/submit-for-review`);
      toast.success("Programa enviado a revisión.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (loading || !data) {
    return (
      <InstructorLayout>
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando editor…</div>
      </InstructorLayout>
    );
  }
  const p = data.program;
  const isEditable = p.review_status !== "approved" && p.review_status !== "pending_review";
  return (
    <InstructorLayout>
      <Link to="/app/instructor/programas" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white mb-3">
        <ArrowLeft className="w-3 h-3" /> Volver a mis programas
      </Link>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">{p.code}</p>
            <h1 className="mt-1 text-2xl font-bold text-white font-display">{p.title}</h1>
            <p className="mt-1 text-xs text-gray-400">{p.format} · {p.modality} · {p.duration_text}</p>
            {p.description && <p className="mt-2 text-sm text-gray-300 max-w-3xl">{p.description}</p>}
            {p.review_status === "rejected" && p.review_notes && (
              <p className="mt-2 text-xs text-red-300"><strong>Notas de revisión:</strong> {p.review_notes}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${STATUS_BADGE[p.review_status] || STATUS_BADGE.draft}`}>
              {p.review_status || "draft"}
            </span>
            {isEditable && data.modules.length > 0 && (
              <button onClick={submitForReview} disabled={submitting}
                      data-testid="editor-submit-review-btn"
                      className="inline-flex items-center gap-1 rounded-md bg-[#F26A21] px-3 py-1.5 text-xs font-semibold text-[#0B0F14] hover:brightness-110">
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Enviar a revisión
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        {/* Sidebar: modules + lessons */}
        <aside className="rounded-2xl border border-white/10 bg-white/5 p-3 overflow-hidden" data-testid="editor-sidebar">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] uppercase tracking-widest font-semibold text-gray-400">Estructura</p>
            {isEditable && (
              <button onClick={addModule} className="inline-flex items-center gap-1 text-[11px] text-[#F26A21] hover:text-white"
                      data-testid="editor-add-module-btn">
                <Plus className="w-3 h-3" /> Módulo
              </button>
            )}
          </div>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            {data.modules.length === 0 ? (
              <p className="text-xs text-gray-500 px-2">Sin módulos. Agrega el primero.</p>
            ) : data.modules.map((m) => (
              <div key={m.id} data-testid={`editor-module-${m.id}`}>
                <div className="flex items-center justify-between px-2 py-1">
                  <p className="text-xs font-bold text-white truncate">{m.order}. {m.title}</p>
                  {isEditable && (
                    <button onClick={() => deleteModule(m.id)} className="text-gray-500 hover:text-red-300 p-1">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="space-y-1 pl-2">
                  {m.lessons.map((l) => {
                    const meta = LESSON_TYPES.find(t => t.value === l.type) || LESSON_TYPES[1];
                    const Icon = meta.Icon;
                    return (
                      <button key={l.id} onClick={() => setSelectedLesson(l)}
                              data-testid={`editor-lesson-${l.id}`}
                              className={`w-full text-left flex items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-white/[0.06] ${selectedLesson?.id === l.id ? "bg-white/[0.08] border-l-2 border-[#F26A21]" : ""}`}>
                        <Icon className="w-3 h-3 text-gray-400" />
                        <span className="truncate text-gray-200">{l.title}</span>
                        {l.source === "ai" && <Wand2 className="w-3 h-3 text-[#F26A21] flex-shrink-0" />}
                      </button>
                    );
                  })}
                  {isEditable && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-gray-400 hover:text-white px-2 py-1 inline-flex items-center gap-1"
                               data-testid={`editor-add-lesson-${m.id}`}>
                        <Plus className="w-3 h-3" /> Lección…
                      </summary>
                      <div className="pl-4 pt-1 space-y-0.5">
                        {LESSON_TYPES.map(t => {
                          const Icon = t.Icon;
                          return (
                            <button key={t.value} onClick={() => addLesson(m.id, t.value)}
                                    className="w-full text-left flex items-center gap-2 px-2 py-1 rounded text-gray-300 hover:bg-white/5">
                              <Icon className="w-3 h-3" /> {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Main: editor */}
        <main className="rounded-2xl border border-white/10 bg-white/5 p-5 min-h-[400px]">
          {!selectedLesson ? (
            <div className="text-center text-sm text-gray-400 py-16">
              <ClipboardCheck className="w-8 h-8 text-gray-500 mx-auto mb-2" />
              Selecciona una lección para editarla o crea una nueva.
            </div>
          ) : (
            <LessonEditor key={selectedLesson.id} lesson={selectedLesson}
                          isEditable={isEditable}
                          onSaved={(l) => { setSelectedLesson(l); load(); }}
                          onDelete={() => deleteLesson(selectedLesson.id)} />
          )}
        </main>
      </div>
    </InstructorLayout>
  );
}

// ---------------------------------------------------------------------------
// Lesson editor (delegates to per-type panels)
// ---------------------------------------------------------------------------
function LessonEditor({ lesson, isEditable, onSaved, onDelete }) {
  const [title, setTitle] = useState(lesson.title);
  const [type, setType] = useState(lesson.type);
  const [duration, setDuration] = useState(lesson.duration_min || 0);
  const [source, setSource] = useState(lesson.source || "manual");
  const [payload, setPayload] = useState(lesson.payload || {});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/instructor/lessons/${lesson.id}`, {
        title, order: lesson.order || 0, type, payload, duration_min: Number(duration) || 0, source,
      });
      toast.success("Lección guardada.");
      onSaved({ ...lesson, title, type, duration_min: Number(duration) || 0, source, payload });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const Panel = {
    video: VideoPanel, text: TextPanel, case_study: CaseStudyPanel,
    presentation: PresentationPanel, quiz: QuizPanel, exam: QuizPanel,
    bibliography: BibliographyPanel, live: LivePanel,
  }[type] || TextPanel;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!isEditable}
               data-testid="lesson-title-input"
               className="flex-1 bg-transparent border-b border-white/10 focus:border-[#F26A21] text-2xl font-bold text-white py-1 outline-none disabled:opacity-70" />
        <select value={source} onChange={(e) => setSource(e.target.value)} disabled={!isEditable}
                data-testid="lesson-source-select"
                className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs text-white">
          <option value="manual">Manual</option>
          <option value="ai">Generado con IA</option>
        </select>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <select value={type} onChange={(e) => { setType(e.target.value); setPayload({}); }} disabled={!isEditable}
                data-testid="lesson-type-select"
                className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs text-white">
          {LESSON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <label className="text-xs text-gray-400 inline-flex items-center gap-1">
          <span>Duración (min):</span>
          <input type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} disabled={!isEditable}
                 data-testid="lesson-duration-input"
                 className="w-16 rounded-md bg-white/5 border border-white/10 px-2 py-1 text-white" />
        </label>
      </div>

      <Panel payload={payload} setPayload={setPayload} disabled={!isEditable} lessonId={lesson.id} />

      {isEditable && (
        <div className="mt-6 flex justify-between">
          <button onClick={onDelete} data-testid="lesson-delete-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-500/40 text-red-300 text-xs hover:bg-red-500/10">
            <Trash2 className="w-3 h-3" /> Eliminar
          </button>
          <button onClick={save} disabled={saving} data-testid="lesson-save-btn"
                  className="inline-flex items-center gap-2 rounded-md bg-[#F26A21] px-4 py-2 text-sm font-semibold text-[#0B0F14] hover:brightness-110 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Type panels ----------
function VideoPanel({ payload, setPayload, disabled }) {
  const p = payload || {};
  const setP = (k, v) => setPayload({ ...p, [k]: v });
  const vsrc = p.video_source_type || "youtube";
  const preview = () => {
    if (!p.video_url) return null;
    if (vsrc === "youtube") {
      const m = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/.exec(p.video_url);
      if (!m) return null;
      return <iframe src={`https://www.youtube.com/embed/${m[1]}`} className="w-full aspect-video rounded-lg" allowFullScreen title="preview" />;
    }
    if (vsrc === "vimeo") {
      const m = /vimeo\.com\/(?:video\/)?(\d+)/.exec(p.video_url);
      if (!m) return null;
      return <iframe src={`https://player.vimeo.com/video/${m[1]}`} className="w-full aspect-video rounded-lg" allowFullScreen title="preview" />;
    }
    return null;
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <select value={vsrc} onChange={(e) => setP("video_source_type", e.target.value)} disabled={disabled}
                data-testid="video-source-select"
                className="col-span-1 rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
          <option value="youtube">YouTube</option>
          <option value="vimeo">Vimeo</option>
          <option value="upload">Subir archivo (500MB máx)</option>
          <option value="dcc_recording">Grabación DCC</option>
        </select>
        <input value={p.video_url || ""} onChange={(e) => setP("video_url", e.target.value)} disabled={disabled}
               placeholder="URL del video (YouTube/Vimeo)"
               data-testid="video-url-input"
               className="col-span-2 rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
      </div>
      {vsrc === "upload" && (
        <FileUploader kind="lesson_video" resourceType="lesson" compact
                      multiple={false}
                      onUploaded={(f) => setP("video_url", `${BACKEND_URL}${f.url}`) & setP("video_source_type", "upload")}
                      buttonLabel="Subir video (≤500MB)" hint={null} />
      )}
      <textarea value={p.description || ""} onChange={(e) => setP("description", e.target.value)} disabled={disabled}
                rows={2}
                placeholder="Contexto / descripción de la lección (opcional)"
                className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
      <div>{preview()}</div>
    </div>
  );
}

function TextPanel({ payload, setPayload, disabled }) {
  const md = payload?.body_markdown || "";
  return (
    <div>
      <textarea value={md} onChange={(e) => setPayload({ ...payload, body_markdown: e.target.value })}
                disabled={disabled} rows={14}
                data-testid="text-body-input"
                placeholder="Escribe la lección en Markdown. Soporta **negrita**, *cursiva*, listas y enlaces."
                className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white font-mono" />
      <p className="mt-1 text-[11px] text-gray-500">Formato: Markdown. Se renderizará al alumno.</p>
    </div>
  );
}

function CaseStudyPanel({ payload, setPayload, disabled }) {
  const p = payload || {};
  const setP = (k, v) => setPayload({ ...p, [k]: v });
  const prompts = p.discussion_prompts || [];
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-semibold text-gray-300">Descripción del caso (Markdown)</label>
        <textarea rows={8} value={p.body_markdown || ""} onChange={(e) => setP("body_markdown", e.target.value)} disabled={disabled}
                  data-testid="case-body-input"
                  placeholder="### Contexto\n\nDescribe el caso, la empresa, la situación…"
                  className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white font-mono" />
      </div>
      <div>
        <label className="text-xs font-semibold text-gray-300">Preguntas de discusión</label>
        <div className="mt-1 space-y-1">
          {prompts.map((q, i) => (
            <div key={i} className="flex gap-2">
              <input value={q} onChange={(e) => { const arr = [...prompts]; arr[i] = e.target.value; setP("discussion_prompts", arr); }}
                     disabled={disabled}
                     className="flex-1 rounded-md bg-white/5 border border-white/10 px-3 py-1.5 text-sm text-white" />
              {!disabled && (
                <button onClick={() => setP("discussion_prompts", prompts.filter((_, j) => j !== i))}
                        className="text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
          {!disabled && (
            <button onClick={() => setP("discussion_prompts", [...prompts, ""])}
                    data-testid="case-add-prompt"
                    className="text-xs text-[#F26A21] hover:text-white inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Agregar pregunta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PresentationPanel({ payload, setPayload, disabled, lessonId }) {
  const p = payload || {};
  const slides = p.slides || [];
  const [exporting, setExporting] = useState(false);
  const setSlide = (i, k, v) => {
    const arr = [...slides]; arr[i] = { ...arr[i], [k]: v };
    setPayload({ ...p, slides: arr });
  };
  const addSlide = () => setPayload({ ...p, slides: [...slides, { title: `Slide ${slides.length + 1}`, body_markdown: "", image_url: null, notes: "" }] });
  const removeSlide = (i) => setPayload({ ...p, slides: slides.filter((_, j) => j !== i) });
  const exportPptx = async () => {
    setExporting(true);
    try {
      const r = await api.post(`/instructor/lessons/${lessonId}/pptx-export`);
      toast.warning("PPTX MOCKED · " + r.data.message, { duration: 6000 });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setExporting(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {!disabled && (
          <button onClick={addSlide} data-testid="pres-add-slide"
                  className="inline-flex items-center gap-1 text-xs text-[#F26A21] hover:text-white">
            <Plus className="w-3 h-3" /> Slide
          </button>
        )}
        <button onClick={exportPptx} disabled={exporting}
                data-testid="pres-export-pptx"
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-white/20 text-white hover:bg-white/10">
          {exporting && <Loader2 className="w-3 h-3 animate-spin" />}
          Exportar PPTX (MOCKED)
        </button>
      </div>
      <div className="space-y-4">
        {slides.length === 0 && <p className="text-xs text-gray-500">Sin slides. Agrega uno.</p>}
        {slides.map((s, i) => (
          <div key={i} data-testid={`pres-slide-${i}`}
               className="rounded-lg border border-white/10 bg-black/30 p-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Slide {i + 1}</p>
              {!disabled && (
                <button onClick={() => removeSlide(i)} className="text-red-300 p-1"><Trash2 className="w-3 h-3" /></button>
              )}
            </div>
            <input value={s.title || ""} onChange={(e) => setSlide(i, "title", e.target.value)} disabled={disabled}
                   className="mt-2 w-full bg-transparent border-b border-white/10 py-1 text-sm font-bold text-white outline-none" placeholder="Título del slide" />
            <textarea rows={4} value={s.body_markdown || ""} onChange={(e) => setSlide(i, "body_markdown", e.target.value)} disabled={disabled}
                      className="mt-2 w-full rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs text-white font-mono"
                      placeholder="Contenido del slide (Markdown)…" />
            <input value={s.notes || ""} onChange={(e) => setSlide(i, "notes", e.target.value)} disabled={disabled}
                   className="mt-2 w-full rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs text-gray-300"
                   placeholder="Notas del ponente" />
          </div>
        ))}
      </div>
    </div>
  );
}

function QuizPanel({ payload, setPayload, disabled }) {
  const p = payload || {};
  const questions = p.questions || [];
  const setQ = (i, k, v) => {
    const arr = [...questions]; arr[i] = { ...arr[i], [k]: v };
    setPayload({ ...p, questions: arr });
  };
  const setOption = (qi, oi, v) => {
    const arr = [...questions];
    const opts = [...(arr[qi].options || [])]; opts[oi] = v;
    arr[qi] = { ...arr[qi], options: opts };
    setPayload({ ...p, questions: arr });
  };
  const addQ = () => setPayload({
    ...p, questions: [...questions, {
      id: crypto.randomUUID(),
      question: "", options: ["", "", "", ""], correct_index: 0, explanation: "",
    }],
  });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <label className="text-xs font-semibold text-gray-300">Puntaje mínimo
          <input type="number" min="0" max="100" value={p.passing_score ?? 70}
                 onChange={(e) => setPayload({ ...p, passing_score: Number(e.target.value) })}
                 disabled={disabled}
                 className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-2 py-1 text-white" /></label>
        {p.is_exam !== undefined || payload?.is_exam ? (
          <label className="text-xs font-semibold text-gray-300">Intentos máx.
            <input type="number" min="1" max="10" value={p.max_attempts ?? 1}
                   onChange={(e) => setPayload({ ...p, max_attempts: Number(e.target.value) })}
                   disabled={disabled}
                   className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-2 py-1 text-white" /></label>
        ) : null}
        {!disabled && (
          <button onClick={addQ} data-testid="quiz-add-q"
                  className="self-end text-xs text-[#F26A21] hover:text-white inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Pregunta</button>
        )}
      </div>
      <div className="space-y-4">
        {questions.map((q, qi) => (
          <div key={q.id || qi} data-testid={`quiz-q-${qi}`}
               className="rounded-lg border border-white/10 bg-black/30 p-3">
            <div className="flex items-start justify-between gap-2">
              <textarea rows={2} value={q.question || ""} onChange={(e) => setQ(qi, "question", e.target.value)}
                        disabled={disabled} className="flex-1 rounded-md bg-white/5 border border-white/10 px-2 py-1 text-sm text-white"
                        placeholder={`Pregunta ${qi + 1}`} />
              {!disabled && (
                <button onClick={() => setPayload({ ...p, questions: questions.filter((_, j) => j !== qi) })}
                        className="text-red-300 p-1"><Trash2 className="w-3 h-3" /></button>
              )}
            </div>
            <div className="mt-2 space-y-1">
              {(q.options || []).map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input type="radio" name={`correct-${qi}`}
                         checked={q.correct_index === oi} onChange={() => setQ(qi, "correct_index", oi)}
                         disabled={disabled}
                         className="accent-[#F26A21]" />
                  <input value={opt} onChange={(e) => setOption(qi, oi, e.target.value)} disabled={disabled}
                         placeholder={`Opción ${oi + 1}`}
                         className="flex-1 rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs text-white" />
                </div>
              ))}
            </div>
            <textarea rows={2} value={q.explanation || ""} onChange={(e) => setQ(qi, "explanation", e.target.value)}
                      disabled={disabled}
                      className="mt-2 w-full rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs text-gray-300"
                      placeholder="Explicación (se muestra al alumno tras responder)" />
          </div>
        ))}
        {questions.length === 0 && <p className="text-xs text-gray-500">Sin preguntas. Agrega la primera.</p>}
      </div>
    </div>
  );
}

function BibliographyPanel({ payload, setPayload, disabled }) {
  const p = payload || {};
  const refs = p.references || [];
  const setRef = (i, k, v) => {
    const arr = [...refs]; arr[i] = { ...arr[i], [k]: v };
    setPayload({ ...p, references: arr });
  };
  return (
    <div className="space-y-2">
      {refs.map((r, i) => (
        <div key={i} data-testid={`biblio-ref-${i}`}
             className="rounded-lg border border-white/10 bg-black/30 p-3">
          <div className="grid sm:grid-cols-2 gap-2">
            <input value={r.title || ""} onChange={(e) => setRef(i, "title", e.target.value)} disabled={disabled}
                   placeholder="Título" className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-sm text-white" />
            <input value={r.author || ""} onChange={(e) => setRef(i, "author", e.target.value)} disabled={disabled}
                   placeholder="Autor(es)" className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-sm text-white" />
            <input type="number" value={r.year || ""} onChange={(e) => setRef(i, "year", Number(e.target.value) || null)} disabled={disabled}
                   placeholder="Año" className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-sm text-white" />
            <input value={r.url || ""} onChange={(e) => setRef(i, "url", e.target.value)} disabled={disabled}
                   placeholder="URL / DOI" className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-sm text-white" />
          </div>
          <input value={r.notes || ""} onChange={(e) => setRef(i, "notes", e.target.value)} disabled={disabled}
                 placeholder="Notas" className="mt-2 w-full rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs text-gray-300" />
          {!disabled && (
            <button onClick={() => setPayload({ ...p, references: refs.filter((_, j) => j !== i) })}
                    className="mt-2 text-xs text-red-300 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Quitar</button>
          )}
        </div>
      ))}
      {!disabled && (
        <button onClick={() => setPayload({ ...p, references: [...refs, { title: "", author: "", year: null, url: "", notes: "" }] })}
                data-testid="biblio-add-ref"
                className="text-xs text-[#F26A21] hover:text-white inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Referencia</button>
      )}
    </div>
  );
}

function LivePanel({ payload, setPayload, disabled }) {
  const p = payload || {};
  const setP = (k, v) => setPayload({ ...p, [k]: v });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-gray-300">Fecha y hora
          <input type="datetime-local" value={p.starts_at || ""} onChange={(e) => setP("starts_at", e.target.value)}
                 disabled={disabled}
                 className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-2 py-1 text-white" /></label>
        <label className="text-xs font-semibold text-gray-300">Duración (min)
          <input type="number" min="15" max="240" value={p.duration_min || 60}
                 onChange={(e) => setP("duration_min", Number(e.target.value) || 60)}
                 disabled={disabled}
                 className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-2 py-1 text-white" /></label>
      </div>
      <p className="text-[11px] text-gray-500">
        El día del evento, los alumnos entrarán a la Sala DCC directamente desde la lección.
      </p>
    </div>
  );
}
